import { State, Action, StateContext, Selector, Store } from '@ngxs/store';
import { GalleryStateModel } from './gallery.state.model';
import { GallerySearchMode } from '../models/gallery-search-mode.enum';
import { GalleryViewModeDefault } from '../models/gallery-view-mode.enum';
import { GalleryActions } from './gallery.actions';
import { OrganizationsService } from 'src/app/organizations/organizations.service';
import { Observable } from 'rxjs';
import { ModulesService } from 'src/app/modules/modules.service';
import { finalize, map } from 'rxjs/operators';
import { ExploreService } from 'src/app/explore/explore.service';
import { BookmarkModel } from 'src/app/bookmarks/models/bookmark.model';
import { BookmarksService } from 'src/app/bookmarks/bookmarks.service';

import { sortModels } from 'src/app/bookmarks/models/bookmark-sort.model';
import {
  bookmarkSortTitleAsc,
  bookmarkSortRelevance,
  bookmarkSortRecent,
  bookmarkSortTitleDesc,
  bookmarkSortAuthorAsc,
  bookmarkSortAuthorDesc,
} from 'src/app/bookmarks/data/bookmark-sort';
import { SearchType } from '../models/search-type.enum';
import { get, isNil } from 'lodash';
import { SearchOptions } from '../models/search-options';
import { GalleryResultsModel } from '../models/gallery-results.model';
import { AuthState } from 'src/app/auth/state/auth/auth.state';
import { Injectable } from '@angular/core';
import { AuthStateModel } from 'src/app/auth/state/auth/auth.state.model';
import { ContentType } from 'src/app/story/models/content-type.enum';

const initialState: GalleryStateModel = {
  loading: true,
  searchMode: GallerySearchMode.Query,
  viewMode: GalleryViewModeDefault,
  searchResults: { total: 0, items: [] },
  checkedResults: [],
  searchConfig: null,
  searchOptions: null,
  sidenav: {
    enabled: true,
    open: true,
  },
  bulkActions: {
    enabled: true,
    items: [],
  },
  cardActions: {
    enabled: true,
  },
};

@State<GalleryStateModel>({
  name: 'gallery',
  defaults: initialState,
})
@Injectable()
export class GalleryState {
  /**
   * Selectors
   */
  @Selector()
  static getLoading(state: GalleryStateModel) {
    return state.loading;
  }

  @Selector()
  static getSearchMode(state: GalleryStateModel) {
    return state.searchMode;
  }

  @Selector()
  static getSearchConfig(state: GalleryStateModel) {
    return state.searchConfig;
  }

  @Selector()
  static getSearchOptions(state: GalleryStateModel) {
    return state.searchOptions;
  }

  @Selector()
  static getSearchType(state: GalleryStateModel) {
    return state.searchConfig ? state.searchConfig.type : null;
  }

  @Selector()
  static getSearchGroup(state: GalleryStateModel) {
    switch (state.searchConfig?.type) {
      case SearchType.Category:
      case SearchType.Community:
      case SearchType.Internal:
      case SearchType.Loft:
        return ContentType.Module;

      case SearchType.Collection:
      case SearchType.Private:
      case SearchType.Organization:
        return ContentType.Bookmark;
    }
    
  }

  @Selector()
  static getViewMode(state: GalleryStateModel) {
    return state.viewMode;
  }

  @Selector()
  static getSort(state: GalleryStateModel) {
    return state.searchOptions.sortBy;
  }

  @Selector()
  static getSortOptions(state: GalleryStateModel) {
    const searchType = state.searchConfig.type;

    switch (searchType) {
      case SearchType.Loft:
        return [bookmarkSortTitleAsc, bookmarkSortTitleDesc];

      case SearchType.Category:
        return [
          bookmarkSortRelevance,
          bookmarkSortRecent,
          bookmarkSortTitleAsc,
          bookmarkSortTitleDesc
        ];

      default:
        return [
          bookmarkSortRecent,
          bookmarkSortTitleAsc,
          bookmarkSortTitleDesc,
          bookmarkSortAuthorAsc,
          bookmarkSortAuthorDesc
        ];
    }
  }

  @Selector()
  static getSidenav(state: GalleryStateModel) {
    return state.sidenav;
  }

  @Selector()
  static getBulkActions(state: GalleryStateModel) {
    return state.bulkActions;
  }

  @Selector()
  static getCardActions(state: GalleryStateModel) {
    return state.cardActions;
  }

  @Selector([AuthState])
  static getFilteredResults(state: GalleryStateModel, authState: AuthStateModel): GalleryResultsModel {
    const { searchResults, searchOptions } = state;
    const { ascending, fields } = searchOptions.sortBy;

    // Determine if we need to filter the results locally or let the server handle it
    let filteredItems = sortModels(searchResults.items, fields, ascending); // Sort
    
    // For free users, filter free items to top so they have a better browsing experience
    if (get(authState, 'userProfile.premium_account_valid') !== true) {
      filteredItems = [
        ...filteredItems.filter(item => item.premium !== true),
        ...filteredItems.filter(item => item.premium === true)
      ];
    }

    // Paginate
    filteredItems = filteredItems
      .slice(
        searchOptions.offset,
        searchOptions.offset + searchOptions.pageSize
      );

    return {
      ...searchResults,
      items: filteredItems,
    };
  }

  @Selector()
  static getAllResults(state: GalleryStateModel, authState: AuthStateModel): GalleryResultsModel {
    const { searchResults, searchOptions } = state;
    const { ascending, fields } = searchOptions.sortBy;

    let items = sortModels(searchResults.items, fields, ascending);

    // For free users, filter free items to top so they have a better browsing experience
    if (get(authState, 'userProfile.premium_account_valid') !== true) {
      items = [
        ...items.filter(item => item.premium !== true),
        ...items.filter(item => item.premium === true)
      ];
    }

    return {
      total: searchResults.total,
      items: items
    };
  }

  @Selector()
  static getCheckedResults(state: GalleryStateModel) {
    return state.checkedResults;
  }

  /**
   * Actions
   */
  @Action(GalleryActions.SetSearchMode)
  setSearchMode(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.SetSearchMode
  ) {
    context.patchState({
      searchMode: action.searchMode,
    });
  }

  @Action(GalleryActions.SetViewMode)
  setViewMode(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.SetViewMode
  ) {
    context.patchState({
      viewMode: action.viewMode,
    });
  }

  @Action(GalleryActions.SetSearchOptions)
  setSearchOptions(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.SetSearchOptions
  ) {
    context.patchState({
      searchOptions: { ...action.options },
    });
  }

  @Action(GalleryActions.SetSearchConfig)
  setSearchConfig(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.SetSearchConfig
  ) {
    const config = action.config || context.getState().searchConfig;
    const options = action.options || context.getState().searchOptions;

    // Don't search when no config is specified, since we don't know where to look
    if (config === null) {
      return;
    }

    context.patchState({
      searchOptions: { ...options },
      searchConfig: { ...config },
    });
  }

  @Action(GalleryActions.SearchStudio)
  searchStudio(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.SearchStudio
  ) {
    const { searchConfig, searchOptions } = context.getState();
    const defaultOptions = new SearchOptions('', bookmarkSortTitleAsc);
    const lang = this.store.selectSnapshot(AuthState.getUserLanguage);

    context.patchState({
      loading: true,
      searchOptions: {
        ...searchOptions,
        pageSize: defaultOptions.pageSize, // Reset pagination on any request
        offset: defaultOptions.offset,
      },
      checkedResults: [], // Always clear checked results on new search
    });

    // Initialize search based on the type of config passed
    // All searches return the same data structure, but rely on different APIs
    let search$ = new Observable<BookmarkModel[]>();

    switch (searchConfig.type) {
      case SearchType.Category:
        // Filter out 'all' param
        search$ = this.exploreService.getAnatomyCategory(
          searchConfig.query,
          searchConfig.categoryId === 'all' ? '' : searchConfig.categoryId,
          searchConfig.tags,
          lang
        );
        break;

      case SearchType.Organization:
        search$ = this.bookmarksService.getOrganizationContent(
          searchConfig.organizationId,
          searchConfig.query
        );
        break;

      case SearchType.Collection:
        search$ = this.organizationsService.getCollectionContent(
          searchConfig.organizationId,
          searchConfig.collectionId,
          searchConfig.query
        );
        break;

      case SearchType.Community:
        search$ = this.bookmarksService.getPublicBookmarks(searchConfig.query);
        break;

      case SearchType.Private:
        search$ = this.bookmarksService.getPrivateBookmarks(searchConfig.query);
        break;

      case SearchType.Internal:
        search$ = this.exploreService.searchInternal(
          searchConfig.query,
          searchConfig.directory
        );
        break;

      case SearchType.Loft:
        const isExplicit = searchConfig.query.indexOf('.json') > 0;

        // Search module services
        search$ = this.modulesService
          .listContents(
            searchConfig.primary,
            searchConfig.secondary,
            isExplicit ? searchConfig.query : '',
            true
          )
          .pipe(
            map((results: BookmarkModel[]) => {
              return !isExplicit
                ? results.filter(
                    (result) => result.title.indexOf(searchConfig.query) >= 0
                  )
                : results;
            })
          );
        break;
    }

    // Perform the search
    search$
      .pipe(finalize(() => context.patchState({ loading: false })))
      .subscribe((results) => {
        context.dispatch(new GalleryActions.SearchSuccess(results));
      });
  }

  @Action(GalleryActions.SearchSuccess)
  searchSuccess(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.SearchSuccess
  ) {
    context.patchState({
      searchResults: {
        items: action.bookmarks,
        total: action.bookmarks.length,
      },
    });
  }

  @Action(GalleryActions.CheckItem)
  checkItem(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.CheckItem
  ) {
    let items = context.getState().checkedResults;
    const exists = items.some((item) => item === action.id);

    if (action.checked && !exists) {
      items = [...items, action.id];
    } else if (!action.checked) {
      items = items.filter((bookmark) => bookmark !== action.id);
    }

    context.patchState({
      checkedResults: items,
    });
  }

  @Action(GalleryActions.CheckAll)
  checkAll(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.CheckAll
  ) {
    const check = isNil(action.checked) ? true : action.checked;
    const ids = context.getState().searchResults.items.map((item) => item.id);

    context.patchState({
      checkedResults: ids,
    });
  }

  @Action(GalleryActions.SetCheckedItems)
  setCheckedItems(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.SetCheckedItems
  ) {
    context.patchState({
      checkedResults: action.ids,
    });
  }

  @Action(GalleryActions.SetSidenav)
  setSidenav(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.SetSidenav
  ) {
    context.patchState({
      sidenav: {
        ...action.sidenav,
      },
    });
  }

  @Action(GalleryActions.SetCardActions)
  setCardActions(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.SetCardActions
  ) {
    context.patchState({
      cardActions: {
        ...action.cardActions,
      },
    });
  }

  @Action(GalleryActions.SetBulkActions)
  setBulkActions(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.SetBulkActions
  ) {
    context.patchState({
      bulkActions: {
        ...action.bulkActions,
      },
    });
  }

  @Action(GalleryActions.ClearResults)
  clearResults(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.ClearResults
  ) {
    context.patchState({
      searchResults: {
        items: [],
        total: 0,
      },
    });
  }

  @Action(GalleryActions.Reset)
  resetGallery(
    context: StateContext<GalleryStateModel>,
    action: GalleryActions.Reset
  ) {
    context.patchState(initialState);
  }

  constructor(
    private organizationsService: OrganizationsService,
    private modulesService: ModulesService,
    private exploreService: ExploreService,
    private bookmarksService: BookmarksService,
    private store: Store
  ) {}
}
